import React from 'react';
import blockCn from 'bem-cn-lite';
import _ from 'lodash';
import {compose} from 'recompose';
import {ServiceStatus} from '@yandex-data-ui/cloud-schemas/build/models/ui-api';
import {HTML} from '@doc-tools/components';
import {
    ImageProps,
    LoadableConfig,
    FetchLoadableDataParams,
    MediaProps,
    PageConstructor,
    Block,
    Pixel,
    ConstructorBlock,
    getThemedValue,
} from '@yandex-data-ui/page-constructor';

import withRouter, {WithRouterProps} from 'hoc/withRouter';
import withLocale, {WithLocaleProps} from 'hoc/withLocale';
import withMobile, {WithMobileProps} from 'hoc/withMobile';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import Meta, {SharingConfig} from 'components/Meta/Meta';
import {getPageConfig, getSharingImageURL} from 'utils';
import componentMap from 'units/constructor-local/componentMap';
import {PageData, PageSolution, PageService, DataSource} from 'units/constructor-local/models';
import PageContext from 'units/constructor-local/contexts/PageContext';
import {ServiceLabel} from 'units/constructor-local/components/';
import {InitialContext} from 'models/app';
import {getPage, GetPageParams, fetchData} from 'units/constructor-local/api';
import {removePrefix} from '../../../../../common/utils/constructor';
import {getHeaderBlock, getPageMenuItems, shouldRenderBlock} from 'units/constructor-local/utils';
import {LocaleData} from 'contexts/LocaleContext';
import {Post, ServicePreviewCard, Event} from 'units/constructor-local/components';
import Menu, {MenuItem} from 'units/constructor-local/components/Menu/Menu';
import metrika from 'counters/metrika';
import pixel from 'counters/pixel';
import {DEFAULT_USER_SETTINGS} from '../../../../../common/constants';

import './Page.scss';
import 'styles/yfm.scss';
import 'styles/yfm-redefinitions.scss';
// to make constructor yfm styles have higher priority than local
import '@yandex-data-ui/page-constructor/styles/yfm.scss';

const b = blockCn('constructor');
const SCROLL_TRIGGER_VALUE = 15;
const DEFAULT_THEME = DEFAULT_USER_SETTINGS.theme;

interface PageMetaConfig {
    meta?: SharingConfig & {
        sharing?: SharingConfig & {
            tag?: string;
            imageText?: string;
        };
    };
}

export interface PageProps {
    data?: PageData;
    serviceConfig?: PageMetaConfig;
    solutionConfig?: PageMetaConfig;
}

type PageInnerProps = PageProps &
    WithRouterProps &
    WithLocaleProps &
    WithTranslationProps &
    WithMobileProps;

interface PageState {
    scrolled: boolean;
    loadableState: Record<string, boolean>;
    hasMenu?: boolean;
}

type PageFetchLoadableDataParams = {
    source: DataSource;
    blockKey: string;
    serviceId?: number;
};

const indexStatuses = [ServiceStatus.PublicPreview, ServiceStatus.Public];
const MIN_LOADABLE_ITEMS_SHOW_COUNT = 3;

class Page extends React.Component<PageInnerProps, PageState> {
    static async getInitialData(locale: LocaleData, {page, params}: GetPageParams) {
        return getPage(locale, {page: removePrefix(page), params});
    }

    static async getInitialProps({componentData, locale}: InitialContext<PageData>) {
        const {service, solution} = componentData ?? {};

        const [serviceConfig, solutionConfig] = await Promise.all([
            service && getPageConfig('services', locale?.code),
            solution && getPageConfig('solutions', locale?.code),
        ]);

        return {
            data: componentData,
            serviceConfig,
            solutionConfig,
        };
    }

    loadableConfig: LoadableConfig = {};

    constructor(props: PageInnerProps) {
        super(props);

        this.state = {
            hasMenu: false,
            scrolled: false,
            loadableState: {},
        };
        this.loadableConfig = {
            [DataSource.Events]: {
                fetch: this.getDataFetcher(DataSource.Events),
                component: Event,
            },
            [DataSource.Blog]: {
                fetch: this.getDataFetcher(DataSource.Blog),
                component: Post,
            },
            [DataSource.Services]: {
                fetch: this.getDataFetcher(DataSource.Services),
                component: ServicePreviewCard,
            },
        };
    }

    componentDidMount() {
        const items = this.getMenuItems();

        if (items && items.length) {
            window.addEventListener('scroll', this.onScroll);
        }
    }

    componentDidUpdate(_prevProps: PageProps, prevState: PageState) {
        const {scrolled} = this.state;
        if (scrolled && scrolled !== prevState.scrolled) {
            this.props.router.events?.emit('pageHeaderHidden');
        }
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.onScroll);
    }

    render() {
        const {data, router, isMobile, locale} = this.props;

        return (
            <main className={b()} key={data?.name}>
                <PageContext.Provider
                    value={{
                        service: data?.service,
                        solution: data?.solution,
                    }}
                >
                    {this.renderMeta()}
                    <PageConstructor
                        content={data?.content}
                        shouldRenderBlock={this.shouldRenderBlock}
                        renderMenu={this.renderMenu}
                        isMobile={isMobile}
                        ssrConfig={{isServer: __IS_SERVER__}}
                        custom={{
                            ...componentMap,
                            loadable: this.loadableConfig,
                        }}
                        location={{hostname: router.hostname, pathname: router.pathname}}
                        locale={locale}
                        metrika={{metrika, pixel: pixel as Pixel}}
                    />
                </PageContext.Provider>
            </main>
        );
    }

    private fetchLoadableData = async (params: PageFetchLoadableDataParams) => {
        const {locale, data: {service, solution} = {}} = this.props;
        const {source, blockKey, serviceId = service?.id} = params;
        const solutionId = solution?.id;
        try {
            const data = await fetchData({locale, serviceId, solutionId}, source);
            const hasData = Array.isArray(data)
                ? data.length >= MIN_LOADABLE_ITEMS_SHOW_COUNT
                : Boolean(data);

            this.onLoadableDataFetched(blockKey, hasData, false);

            return data;
        } catch (error) {
            this.onLoadableDataFetched(blockKey, false, true);
            throw error;
        }
    };

    private onLoadableDataFetched(blockKey: string, hasData: boolean, hasError: boolean) {
        const {loadableState} = this.state;
        this.setState({
            loadableState: {
                ...loadableState,
                [blockKey]: hasData && !hasError,
            },
        });
    }

    private getDataFetcher =
        (source: DataSource) =>
        ({blockKey, serviceId}: FetchLoadableDataParams) =>
            this.fetchLoadableData({source, blockKey, serviceId});

    private shouldRenderBlock = (block: ConstructorBlock, blockKey: string) => {
        const {data: {service} = {}} = this.props;
        const {loadableState} = this.state;

        return shouldRenderBlock(block, service, blockKey, loadableState);
    };

    private renderMenu = () => {
        const items = this.getMenuItems();

        return items && items.length > 1 ? (
            <Menu items={items}>{this.renderMenuTitle()}</Menu>
        ) : null;
    };

    private getMenuItems(): MenuItem[] {
        const {data: {service, solution, content: {blocks = [], menu = undefined} = {}} = {}} =
            this.props;
        const {loadableState} = this.state;

        return menu || Boolean(service || solution)
            ? getPageMenuItems(service, blocks, loadableState)
            : [];
    }

    private renderMeta() {
        const {
            i18nK,
            data: {
                content: {blocks = [], background = {}} = {},
                title = i18nK('constructor_page-default-title'),
                metaDescription,
                keywords,
                noIndex,
                shareTitle,
                shareDescription,
                shareImage,
                service,
                solution,
            } = {},
        } = this.props;

        const themedBackground = getThemedValue(background, DEFAULT_THEME);

        const defaultMeta = {
            title,
            description: metaDescription,
            keywords,
            noIndex,
            image: shareImage,
            sharing: {
                title: shareTitle?.length ? shareTitle : title,
                description: shareDescription,
            },
        };

        const meta =
            (service && this.getServiceMeta(service, themedBackground)) ||
            (solution && this.getSolutionMeta(solution, blocks)) ||
            defaultMeta;

        return <Meta {...meta} />;
    }

    private renderMenuTitle() {
        if (!this.props.data) {
            return null;
        }

        const {service, solution, content} = this.props.data;
        const titleText = content.menu?.title || service?.name || solution?.name;
        let title;

        if (titleText) {
            title = service ? (
                <ServiceLabel {...service} name={titleText} />
            ) : (
                <HTML className={b('menu-title')}>{titleText}</HTML>
            );
        }

        return title;
    }

    private getSolutionMeta(solution: PageSolution, blocks: Block[]) {
        const {solutionConfig, data: {title: metaTitle, metaDescription} = {}} = this.props;

        const {
            name = solutionConfig?.meta?.sharing?.title,
            description = solutionConfig?.meta?.sharing?.description,
        } = solution;

        return this.getMeta(
            'solution-page-share',
            solutionConfig,
            metaTitle,
            metaDescription || description || undefined,
            name ?? undefined,
            description ?? undefined,
            getHeaderBlock(blocks)?.image,
        );
    }

    private getServiceMeta(service: PageService, background: MediaProps) {
        const {serviceConfig, data: {content, title: metaTitle, metaDescription} = {}} = this.props;

        const headerBlock = getHeaderBlock(content?.blocks || []);
        const blockTitle = headerBlock?.title;
        const blockDescription = headerBlock?.description;

        const image = Array.isArray(background.image) ? background.image[0] : background.image;

        const meta = this.getMeta(
            'service-page-share',
            serviceConfig,
            metaTitle || blockTitle,
            metaDescription || blockDescription,
            metaTitle,
            metaDescription,
            image,
        );

        return {
            noIndex: !indexStatuses.includes(service.status),
            ...meta,
        };
    }

    private getMeta(
        sharingSchema: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        config: any,
        title: string | undefined,
        description: string | undefined,
        sharingTitle: string | undefined,
        sharingDescription: string | undefined,
        image: ImageProps | undefined,
    ) {
        const {router, locale, data: {keywords} = {}} = this.props;

        const imageSrc = typeof image === 'string' || !image ? image : image.src;

        return {
            title,
            sectionTitle: config.meta.title,
            description,
            keywords,
            type: 'website',
            sharing: {title: sharingTitle, description: sharingDescription},
            image: getSharingImageURL(locale, router),
            extra: [
                {property: 'share:title', content: sharingTitle},
                {property: 'share:content_image', content: imageSrc},
                {property: 'share:tag', content: config.meta.tag},
                {property: 'share:sharing_schema', content: sharingSchema},
            ],
        };
    }

    // eslint-disable-next-line react/sort-comp, @typescript-eslint/member-ordering
    private onScroll = _.throttle(() => {
        const scrolled = window.scrollY > SCROLL_TRIGGER_VALUE;

        if (scrolled !== this.state.scrolled) {
            this.setState({scrolled});
        }
    }, 300);
}

export default compose<PageInnerProps, PageProps>(
    withRouter,
    withLocale,
    withMobile,
    withTranslation('constructor'),
)(Page);
